import { useEffect, useRef, useState, type ReactNode } from 'react';
import { loadQuiz, saveQuiz } from '../lib/quizStorage';
import {
  EMPTY_FORM,
  FlowContext,
  type FlowState,
  type FormData,
  type QuizResult,
  type QuizResults,
  type ShareImages,
} from './flow';

export function FlowProvider({ children }: { children: ReactNode }) {
  // Hydrate the shared name + completed quizzes from localStorage once on mount.
  // The ID-card fields (major/minor/clubs/photo) stay in-memory only.
  const [persisted] = useState(loadQuiz);
  const [form, setForm] = useState<FormData>(
    persisted.name.firstName || persisted.name.lastName
      ? { ...EMPTY_FORM, ...persisted.name }
      : EMPTY_FORM,
  );
  const [photoDataUrl, setPhoto] = useState<string | null>(null);
  const [cardImageDataUrl, setCardImage] = useState<string | null>(null);
  const [shareImages, setShareImages] = useState<ShareImages | null>(null);
  const [quizResults, setQuizResults] = useState<QuizResults>(persisted.results);
  const [idFormTouched, setIdFormTouched] = useState(false);

  function setName(firstName: string, lastName: string) {
    setForm((prev) => ({ ...prev, firstName, lastName }));
  }

  function setQuizResult(result: QuizResult) {
    setQuizResults((prev) => ({ ...prev, [result.episode]: result }));
  }

  // Persist the shared name + completed quizzes whenever either changes.
  // Persist on change only — skip the initial run, which would just rewrite
  // the bytes we hydrated from on every app boot.
  const hydrated = useRef(false);
  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true;
      return;
    }
    saveQuiz({
      name: { firstName: form.firstName, lastName: form.lastName },
      results: quizResults,
    });
  }, [form.firstName, form.lastName, quizResults]);

  const value: FlowState = {
    form,
    photoDataUrl,
    cardImageDataUrl,
    shareImages,
    setForm,
    setPhoto,
    setCardImage,
    setShareImages,
    hasFormData: Boolean(form.firstName && form.lastName && form.major),
    hasPhoto: photoDataUrl !== null,
    quizResults,
    setQuizResult,
    setName,
    idFormTouched,
    setIdFormTouched,
  };

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>;
}
